import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Field, field } from './Field';

/**
 * Add a customer. The service address is captured HERE rather than in a second step,
 * because a customer with no address can't be given a job — and the person typing this
 * is on the phone with them right now, which is the only moment they have the address.
 */
export function NewCustomerSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (customerId: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'residential' | 'commercial'>('residential');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const reset = () => {
    setKind('residential');
    setDisplayName('');
    setEmail('');
    setPhone('');
    setAddress1('');
    setCity('');
    setState('');
    setZip('');
    setLeadSource('');
    setFailure(null);
  };

  const addProperty = useMutation(trpc.customer.addProperty.mutationOptions());
  const createCustomer = useMutation(trpc.customer.create.mutationOptions());

  const submit = async () => {
    setFailure(null);
    try {
      const customer = await createCustomer.mutateAsync({
        kind,
        displayName: displayName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        leadSource: leadSource.trim() || undefined,
      });
      if (address1.trim()) {
        await addProperty.mutateAsync({
          customerId: customer.id,
          label: 'Service address',
          address1: address1.trim(),
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip: zip.trim() || undefined,
        });
      }
      await qc.invalidateQueries();
      reset();
      onCreated?.(customer.id);
      onClose();
    } catch (e) {
      // The customer may exist while the address failed — say so plainly instead of
      // pretending nothing happened or that everything worked.
      setFailure(e instanceof Error ? e.message : 'Could not save this customer');
    }
  };

  const busy = createCustomer.isPending || addProperty.isPending;
  const valid = displayName.trim().length > 0;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New customer"
      footer={
        <Button
          className="w-full"
          onClick={() => void submit()}
          disabled={!valid || busy}
        >
          {busy ? 'Saving…' : 'Add customer'}
        </Button>
      }
    >
      <div className="space-y-3">
        {failure ? <Notice tone="danger">{failure}</Notice> : null}

        <Field label="Type">
          <select
            className={field}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'residential' | 'commercial')}
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </Field>

        <Field label={kind === 'commercial' ? 'Business name' : 'Name'}>
          <input
            className={field}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={
              kind === 'commercial' ? 'Northgate Property Mgmt' : 'Dana Whitfield'
            }
            autoFocus
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Phone">
            <input
              className={field}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="614-555-0100"
              inputMode="tel"
            />
          </Field>
          <Field label="Email">
            <input
              className={field}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Optional"
              inputMode="email"
            />
          </Field>
        </div>

        <Field label="Service address">
          <input
            className={field}
            value={address1}
            onChange={(e) => setAddress1(e.target.value)}
            placeholder="18 Oak Lane"
          />
        </Field>

        <div className="grid grid-cols-[1fr_70px_90px] gap-2">
          <Field label="City">
            <input
              className={field}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </Field>
          <Field label="State">
            <input
              className={field}
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="OH"
            />
          </Field>
          <Field label="ZIP">
            <input
              className={field}
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
          </Field>
        </div>

        {/* Asked at intake, because nobody ever goes back and fills it in. It is what
            makes "where does our work come from" answerable a year from now. */}
        <Field label="How did they find you">
          <input
            className={field}
            value={leadSource}
            onChange={(e) => setLeadSource(e.target.value)}
            placeholder="Referral, Google, repeat customer…"
            list="lead-source-suggestions"
          />
          <datalist id="lead-source-suggestions">
            <option value="Referral" />
            <option value="Google" />
            <option value="Repeat customer" />
            <option value="Truck / yard sign" />
            <option value="Facebook" />
            <option value="Nextdoor" />
          </datalist>
        </Field>

        <p className="text-xs text-muted-foreground">
          Only the name is required — everything else can be filled in later.
        </p>
      </div>
    </Sheet>
  );
}
